/**
 * ============================================================================
 * TuTaxi Madrid Norte - Páginas Legales
 * ============================================================================
 * 
 * Componentes para las páginas legales requeridas:
 * - Aviso Legal
 * - Política de Privacidad  
 * - Política de Cookies
 * 
 * IMPORTANTE: Estos textos son plantillas genéricas. 
 * Se recomienda revisión por un abogado antes de publicar.
 */

import { Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail, MapPin } from "lucide-react";

// ============================================================================
// DATOS DEL TITULAR
// ============================================================================

const DATOS_TITULAR = {
  nombre: "Jose Antonio Villacorta Valentin",
  nombreComercial: "Taxi Madrid Norte",
  direccion: "Madrid, España",
  telefono: "639 759 160",
  email: "reservastutaximadrid@gmail.com", // Actualizar si tienes otro email
  web: "https://taximadridnorte.es",
};

// ============================================================================
// COMPONENTE: Header de Páginas Legales
// ============================================================================

const LegalHeader = ({ title }) => (
  <header className="bg-white shadow-sm sticky top-0 z-50">
    <div className="container mx-auto px-4 md:px-6 max-w-4xl">
      <div className="flex items-center justify-between h-16">
        <Link 
          to="/" 
          className="flex items-center gap-2 text-[#1A1A1A] hover:text-[#F5D109] transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-semibold">Volver</span>
        </Link>
        <span className="font-heading text-lg font-bold text-[#1A1A1A]">
          TuTaxi<span className="text-[#F5D109]">Madrid</span>Norte
        </span>
      </div>
    </div>
    <div className="bg-[#F5D109] py-8 md:py-12">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase text-black">
          {title}
        </h1>
      </div>
    </div>
  </header>
);

// ============================================================================
// COMPONENTE: Footer de Páginas Legales
// ============================================================================

const LegalFooter = () => (
  <footer className="bg-[#1A1A1A] text-white py-8">
    <div className="container mx-auto px-4 md:px-6 max-w-4xl">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-white/60 text-sm">
          © {new Date().getFullYear()} {DATOS_TITULAR.nombreComercial}
        </p>
        <div className="flex gap-4 text-sm">
          <Link to="/aviso-legal" className="text-white/60 hover:text-[#F5D109]">Aviso Legal</Link>
          <Link to="/privacidad" className="text-white/60 hover:text-[#F5D109]">Privacidad</Link>
          <Link to="/cookies" className="text-white/60 hover:text-[#F5D109]">Cookies</Link>
        </div>
      </div>
    </div>
  </footer>
);

// ============================================================================
// PÁGINA: Aviso Legal
// ============================================================================

export const AvisoLegal = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <LegalHeader title="Aviso Legal" />
      
      <main className="container mx-auto px-4 md:px-6 max-w-4xl py-8 md:py-12">
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-10 space-y-8">
          
          {/* Identificación del titular */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              1. Identificación del Titular
            </h2>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <p><strong>Titular:</strong> {DATOS_TITULAR.nombre}</p>
              <p><strong>Nombre comercial:</strong> {DATOS_TITULAR.nombreComercial}</p>
              <p className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#F5D109]" />
                <strong>Ubicación:</strong> {DATOS_TITULAR.direccion}
              </p>
              <p className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#F5D109]" />
                <strong>Teléfono:</strong> {DATOS_TITULAR.telefono}
              </p>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#F5D109]" />
                <strong>Email:</strong> {DATOS_TITULAR.email}
              </p>
            </div>
          </section>

          {/* Objeto */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              2. Objeto
            </h2>
            <p className="text-[#666666] leading-relaxed">
              El presente Aviso Legal regula el uso del sitio web <strong>{DATOS_TITULAR.web}</strong>, 
              del que es titular {DATOS_TITULAR.nombre}. La navegación por el sitio web atribuye 
              la condición de usuario del mismo e implica la aceptación plena y sin reservas de 
              todas las disposiciones incluidas en este Aviso Legal.
            </p>
          </section>

          {/* Actividad */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              3. Actividad
            </h2>
            <p className="text-[#666666] leading-relaxed">
              {DATOS_TITULAR.nombreComercial} es un servicio de taxi con licencia oficial del 
              Ayuntamiento de Madrid, especializado en las zonas de Mirasierra, Barrio del Pilar 
              y Fuencarral (Madrid Norte). Ofrecemos servicio de transporte de pasajeros con 
              reserva anticipada, traslados al aeropuerto y servicios de larga distancia.
            </p>
          </section>

          {/* Condiciones de uso */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              4. Condiciones de Uso
            </h2>
            <p className="text-[#666666] leading-relaxed mb-4">
              El usuario se compromete a hacer un uso adecuado de los contenidos y servicios 
              que {DATOS_TITULAR.nombreComercial} ofrece a través de su sitio web y a no emplearlos para:
            </p>
            <ul className="list-disc list-inside text-[#666666] space-y-2 ml-4">
              <li>Incurrir en actividades ilícitas, ilegales o contrarias a la buena fe y al orden público.</li>
              <li>Difundir contenidos o propaganda de carácter racista, xenófobo, o cualquier otro que atente contra los derechos humanos.</li>
              <li>Provocar daños en los sistemas físicos y lógicos del sitio web o de terceros.</li>
              <li>Introducir o difundir virus informáticos o cualesquiera otros sistemas que sean susceptibles de causar daños.</li>
            </ul>
          </section>

          {/* Propiedad intelectual */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              5. Propiedad Intelectual e Industrial
            </h2>
            <p className="text-[#666666] leading-relaxed">
              Todos los contenidos del sitio web, incluyendo textos, fotografías, gráficos, 
              imágenes, iconos, tecnología, software, así como su diseño gráfico y códigos fuente, 
              constituyen una obra cuya propiedad pertenece a {DATOS_TITULAR.nombre}, sin que 
              puedan entenderse cedidos al usuario ninguno de los derechos de explotación sobre 
              los mismos más allá de lo estrictamente necesario para el correcto uso del web.
            </p>
          </section>

          {/* Exclusión de responsabilidad */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              6. Exclusión de Responsabilidad
            </h2>
            <p className="text-[#666666] leading-relaxed mb-4">
              {DATOS_TITULAR.nombreComercial} no se hace responsable de:
            </p>
            <ul className="list-disc list-inside text-[#666666] space-y-2 ml-4">
              <li>Los fallos e incidencias en las comunicaciones, borrado o transmisiones incompletas.</li>
              <li>La no operatividad o problemas en la dirección de email facilitada por el usuario.</li>
              <li>Los daños producidos por terceras personas mediante intromisiones ilegítimas.</li>
            </ul>
          </section>

          {/* Tarifas */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              7. Tarifas y Precios
            </h2>
            <p className="text-[#666666] leading-relaxed">
              Las tarifas aplicables son las <strong>Tarifas Oficiales del Ayuntamiento de Madrid</strong>, 
              reguladas por taxímetro homologado. Los suplementos oficiales por servicios al Aeropuerto 
              de Madrid-Barajas, IFEMA y otros destinos especiales se aplicarán según la normativa vigente. 
              Consulte los suplementos oficiales antes de realizar su reserva.
            </p>
          </section>

          {/* Legislación aplicable */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              8. Legislación Aplicable
            </h2>
            <p className="text-[#666666] leading-relaxed">
              Para la resolución de todas las controversias o cuestiones relacionadas con el presente 
              sitio web o de las actividades en él desarrolladas, será de aplicación la legislación española, 
              a la que se someten expresamente las partes, siendo competentes para la resolución de todos 
              los conflictos derivados o relacionados con su uso los Juzgados y Tribunales de Madrid.
            </p>
          </section>

          {/* Última actualización */}
          <p className="text-sm text-[#999999] pt-4 border-t border-gray-200">
            Última actualización: {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
};

// ============================================================================
// PÁGINA: Política de Privacidad
// ============================================================================

export const PoliticaPrivacidad = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <LegalHeader title="Política de Privacidad" />
      
      <main className="container mx-auto px-4 md:px-6 max-w-4xl py-8 md:py-12">
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-10 space-y-8">
          
          {/* Introducción */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              1. Información al Usuario
            </h2>
            <p className="text-[#666666] leading-relaxed">
              <strong>{DATOS_TITULAR.nombre}</strong>, como Responsable del Tratamiento, le informa que, 
              según lo dispuesto en el Reglamento (UE) 2016/679, de 27 de abril (RGPD) y en la L.O. 3/2018, 
              de 5 de diciembre, de protección de datos y garantía de los derechos digitales (LOPDGDD), 
              trataremos sus datos como se describe en esta Política de Privacidad.
            </p>
          </section>

          {/* Responsable */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              2. Responsable del Tratamiento
            </h2>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <p><strong>Identidad:</strong> {DATOS_TITULAR.nombre}</p>
              <p><strong>Nombre comercial:</strong> {DATOS_TITULAR.nombreComercial}</p>
              <p><strong>Dirección:</strong> {DATOS_TITULAR.direccion}</p>
              <p><strong>Teléfono:</strong> {DATOS_TITULAR.telefono}</p>
              <p><strong>Email de contacto:</strong> {DATOS_TITULAR.email}</p>
            </div>
          </section>

          {/* Finalidad */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              3. Finalidad del Tratamiento
            </h2>
            <p className="text-[#666666] leading-relaxed mb-4">
              Tratamos la información que nos facilitan las personas interesadas con las siguientes finalidades:
            </p>
            <ul className="list-disc list-inside text-[#666666] space-y-2 ml-4">
              <li><strong>Gestión de reservas:</strong> Procesar las solicitudes de servicio de taxi realizadas a través del formulario web o WhatsApp.</li>
              <li><strong>Comunicación con el cliente:</strong> Contactar para confirmar reservas, informar sobre el servicio o resolver incidencias.</li>
              <li><strong>Facturación:</strong> Emitir facturas cuando sean solicitadas por el cliente.</li>
              <li><strong>Mejora del servicio:</strong> Analizar el uso del sitio web para mejorar la experiencia del usuario.</li>
            </ul>
          </section>

          {/* Legitimación */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              4. Legitimación del Tratamiento
            </h2>
            <p className="text-[#666666] leading-relaxed">
              La base legal para el tratamiento de sus datos es:
            </p>
            <ul className="list-disc list-inside text-[#666666] space-y-2 ml-4 mt-4">
              <li><strong>Ejecución de un contrato:</strong> El tratamiento es necesario para la prestación del servicio de taxi solicitado.</li>
              <li><strong>Consentimiento del interesado:</strong> Al enviar el formulario de contacto o reserva, usted consiente el tratamiento de sus datos.</li>
              <li><strong>Interés legítimo:</strong> Para mejoras del servicio y análisis estadísticos anonimizados.</li>
            </ul>
          </section>

          {/* Datos recogidos */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              5. Datos Personales Recogidos
            </h2>
            <p className="text-[#666666] leading-relaxed mb-4">
              A través del formulario de reserva recogemos:
            </p>
            <ul className="list-disc list-inside text-[#666666] space-y-2 ml-4">
              <li>Dirección de origen y destino del trayecto</li>
              <li>Fecha y hora del servicio</li>
              <li>Número de teléfono de contacto</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mt-4">
              <strong>No recogemos</strong> datos especialmente protegidos (salud, religión, orientación sexual, etc.).
            </p>
          </section>

          {/* Conservación */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              6. Conservación de los Datos
            </h2>
            <p className="text-[#666666] leading-relaxed">
              Los datos personales proporcionados se conservarán durante el tiempo necesario para prestar 
              el servicio solicitado y, posteriormente, durante los plazos legalmente establecidos para 
              atender posibles responsabilidades derivadas del tratamiento. Los datos de facturación se 
              conservarán durante 5 años según la normativa fiscal vigente.
            </p>
          </section>

          {/* Destinatarios */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              7. Destinatarios de los Datos
            </h2>
            <p className="text-[#666666] leading-relaxed">
              Sus datos <strong>no serán cedidos a terceros</strong>, salvo obligación legal. 
              Los datos de reserva se transmiten únicamente a través de WhatsApp (Meta Platforms) 
              cuando usted utiliza esta vía de comunicación.
            </p>
          </section>

          {/* Derechos */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              8. Derechos del Interesado
            </h2>
            <p className="text-[#666666] leading-relaxed mb-4">
              Usted tiene derecho a:
            </p>
            <ul className="list-disc list-inside text-[#666666] space-y-2 ml-4">
              <li><strong>Acceso:</strong> Conocer qué datos personales tenemos sobre usted.</li>
              <li><strong>Rectificación:</strong> Corregir datos inexactos o incompletos.</li>
              <li><strong>Supresión:</strong> Solicitar la eliminación de sus datos cuando ya no sean necesarios.</li>
              <li><strong>Oposición:</strong> Oponerse al tratamiento de sus datos en determinadas circunstancias.</li>
              <li><strong>Limitación:</strong> Solicitar la limitación del tratamiento en determinados casos.</li>
              <li><strong>Portabilidad:</strong> Recibir sus datos en formato estructurado y de uso común.</li>
            </ul>
            <p className="text-[#666666] leading-relaxed mt-4">
              Para ejercer estos derechos, puede contactarnos en <strong>{DATOS_TITULAR.email}</strong> o 
              llamando al <strong>{DATOS_TITULAR.telefono}</strong>.
            </p>
            <p className="text-[#666666] leading-relaxed mt-4">
              También puede presentar una reclamación ante la <strong>Agencia Española de Protección de Datos</strong> (AEPD) 
              si considera que sus derechos no han sido atendidos correctamente: <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-[#F5D109] hover:underline">www.aepd.es</a>
            </p>
          </section>

          {/* Última actualización */}
          <p className="text-sm text-[#999999] pt-4 border-t border-gray-200">
            Última actualización: {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
};

// ============================================================================
// PÁGINA: Política de Cookies
// ============================================================================

export const PoliticaCookies = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <LegalHeader title="Política de Cookies" />
      
      <main className="container mx-auto px-4 md:px-6 max-w-4xl py-8 md:py-12">
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-10 space-y-8">
          
          {/* Qué son las cookies */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              1. ¿Qué son las Cookies?
            </h2>
            <p className="text-[#666666] leading-relaxed">
              Las cookies son pequeños archivos de texto que los sitios web almacenan en su dispositivo 
              (ordenador, tablet o móvil) cuando los visita. Sirven para que el sitio web recuerde 
              información sobre su visita, como su idioma preferido y otras opciones, lo que puede 
              facilitar su próxima visita y hacer que el sitio le resulte más útil.
            </p>
          </section>

          {/* Cookies que utilizamos */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              2. Cookies que Utilizamos
            </h2>
            <p className="text-[#666666] leading-relaxed mb-4">
              En este sitio web utilizamos las siguientes cookies:
            </p>
            
            {/* Tabla de cookies */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-[#F5D109]/10">
                    <th className="border border-gray-200 px-4 py-2 text-left font-semibold">Cookie</th>
                    <th className="border border-gray-200 px-4 py-2 text-left font-semibold">Tipo</th>
                    <th className="border border-gray-200 px-4 py-2 text-left font-semibold">Finalidad</th>
                    <th className="border border-gray-200 px-4 py-2 text-left font-semibold">Duración</th>
                  </tr>
                </thead>
                <tbody className="text-[#666666]">
                  <tr>
                    <td className="border border-gray-200 px-4 py-2">_ga</td>
                    <td className="border border-gray-200 px-4 py-2">Analítica</td>
                    <td className="border border-gray-200 px-4 py-2">Google Analytics - Distinguir usuarios</td>
                    <td className="border border-gray-200 px-4 py-2">2 años</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-4 py-2">_ga_*</td>
                    <td className="border border-gray-200 px-4 py-2">Analítica</td>
                    <td className="border border-gray-200 px-4 py-2">Google Analytics 4 - Mantener estado de sesión</td>
                    <td className="border border-gray-200 px-4 py-2">2 años</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-4 py-2">_gid</td>
                    <td className="border border-gray-200 px-4 py-2">Analítica</td>
                    <td className="border border-gray-200 px-4 py-2">Google Analytics - Distinguir usuarios</td>
                    <td className="border border-gray-200 px-4 py-2">24 horas</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Tipos de cookies */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              3. Tipos de Cookies según su Finalidad
            </h2>
            <ul className="space-y-4 text-[#666666]">
              <li>
                <strong className="text-[#1A1A1A]">Cookies técnicas:</strong> Son aquellas que permiten al usuario 
                la navegación a través del sitio web y la utilización de las diferentes opciones o servicios que 
                en ella existen. Actualmente no utilizamos cookies técnicas propias.
              </li>
              <li>
                <strong className="text-[#1A1A1A]">Cookies analíticas:</strong> Son aquellas que permiten realizar 
                el seguimiento y análisis del comportamiento de los usuarios del sitio web. La información recogida 
                mediante este tipo de cookies se utiliza para mejorar el funcionamiento del sitio web.
              </li>
            </ul>
          </section>

          {/* Gestión de cookies */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              4. Cómo Gestionar las Cookies
            </h2>
            <p className="text-[#666666] leading-relaxed mb-4">
              Puede permitir, bloquear o eliminar las cookies instaladas en su equipo mediante la configuración 
              de las opciones del navegador instalado en su ordenador:
            </p>
            <ul className="list-disc list-inside text-[#666666] space-y-2 ml-4">
              <li>
                <a href="https://support.google.com/chrome/answer/95647?hl=es" target="_blank" rel="noopener noreferrer" className="text-[#F5D109] hover:underline">
                  Google Chrome
                </a>
              </li>
              <li>
                <a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias" target="_blank" rel="noopener noreferrer" className="text-[#F5D109] hover:underline">
                  Mozilla Firefox
                </a>
              </li>
              <li>
                <a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#F5D109] hover:underline">
                  Safari
                </a>
              </li>
              <li>
                <a href="https://support.microsoft.com/es-es/windows/eliminar-y-administrar-cookies-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer" className="text-[#F5D109] hover:underline">
                  Microsoft Edge
                </a>
              </li>
            </ul>
            <p className="text-[#666666] leading-relaxed mt-4">
              Si desactiva las cookies, es posible que no pueda utilizar algunas funcionalidades del sitio web.
            </p>
          </section>

          {/* Cookies de terceros */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              5. Cookies de Terceros
            </h2>
            <p className="text-[#666666] leading-relaxed">
              Este sitio web utiliza servicios de análisis de terceros. En particular, utilizamos 
              <strong> Google Analytics</strong>, un servicio de análisis web prestado por Google, Inc. 
              Google Analytics utiliza cookies para ayudarnos a analizar el uso que hacen los usuarios 
              del sitio web. Puede obtener más información sobre las cookies de Google en: 
              <a href="https://policies.google.com/technologies/cookies?hl=es" target="_blank" rel="noopener noreferrer" className="text-[#F5D109] hover:underline ml-1">
                Política de cookies de Google
              </a>
            </p>
          </section>

          {/* Actualización */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">
              6. Actualización de la Política de Cookies
            </h2>
            <p className="text-[#666666] leading-relaxed">
              Esta Política de Cookies puede ser actualizada, por lo que le recomendamos revisarla 
              de forma periódica. Cualquier cambio significativo será comunicado a través del sitio web.
            </p>
          </section>

          {/* Última actualización */}
          <p className="text-sm text-[#999999] pt-4 border-t border-gray-200">
            Última actualización: {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
};

export default { AvisoLegal, PoliticaPrivacidad, PoliticaCookies };
